import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import Button from "@/components/ui/button";
import type { ReviewFilters, ReviewSource, ReviewType, ReviewContentType, ReviewRatingFilter, ReviewTagsMode, ReviewSort, Review, ReviewAvatarFilter, ReviewCompanyLogoFilter } from "@/modules/review/types";
import type { Tag } from "@/modules/tag/types";
import { getTagColorStyles } from "@/modules/tag/colors";
import { useReviews } from "@/modules/review/hooks/use-reviews";
import type { ReviewSelectionFilters, ReviewSelectionSort } from "@/modules/review-selection/types";
import AvailableReviewItem from "./available-review-item";

// Sort options
type SortOption = "newest" | "oldest" | "highest_rated" | "lowest_rated" | "video_first" | "text_first";

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
	{ value: "newest", label: "Newest first" },
	{ value: "oldest", label: "Oldest first" },
	{ value: "highest_rated", label: "Highest rated first" },
	{ value: "lowest_rated", label: "Lowest rated first" },
	{ value: "video_first", label: "Video first" },
	{ value: "text_first", label: "Text first" },
];

// Convert SortOption to ReviewSort for API calls
function convertSortOptionToReviewSort(sortOption: SortOption): ReviewSort {
	switch (sortOption) {
		case "newest":
			return { order_by: "date", direction: "desc" };
		case "oldest":
			return { order_by: "date", direction: "asc" };
		case "highest_rated":
			return { order_by: "rating", direction: "desc" };
		case "lowest_rated":
			return { order_by: "rating", direction: "asc" };
		case "video_first":
			return { order_by: "content_type", direction: "asc" };
		case "text_first":
			return { order_by: "content_type", direction: "desc" };
		default:
			return { order_by: "date", direction: "desc" };
	}
}

// Convert WallOfLoveSort to SortOption for display
function convertReviewSortToSortOption(sort: ReviewSelectionSort | null): SortOption {
	if (!sort || sort.length === 0) return "newest";
	const { field, direction } = sort[0];
	if (field === "date" && direction === "desc") return "newest";
	if (field === "date" && direction === "asc") return "oldest";
	if (field === "rating" && direction === "desc") return "highest_rated";
	if (field === "rating" && direction === "asc") return "lowest_rated";
	if (field === "content_type" && direction === "asc") return "video_first";
	if (field === "content_type" && direction === "desc") return "text_first";
	return "newest";
}

// Convert SortOption to WallOfLoveSort for saving
function convertSortOptionToWallSort(sortOption: SortOption): ReviewSelectionSort {
	const reviewSort = convertSortOptionToReviewSort(sortOption);
	return [
		{
			field: reviewSort.order_by,
			direction: reviewSort.direction,
		},
	];
}

// Convert WallOfLoveFilters to ReviewFilters for API calls
function convertWallFiltersToReviewFilters(wallFilters: ReviewSelectionFilters | null): ReviewFilters {
	const reviewFilters: ReviewFilters = {};

	if (!wallFilters?.groups) return reviewFilters;

	if (wallFilters.groups.rating) {
		const { value, operator } = wallFilters.groups.rating;
		if (operator === "gte" && typeof value === "number") {
			const ratingMap: Record<number, ReviewRatingFilter> = {
				5: "5_and_above",
				4: "4_and_above",
				3: "3_and_above",
				2: "2_and_above",
				1: "1_and_above",
			};
			if (ratingMap[value]) {
				reviewFilters.rating = ratingMap[value];
			}
		}
	}

	if (wallFilters.groups.type) {
		const { value, operator } = wallFilters.groups.type;
		if (operator === "eq" && typeof value === "string") {
			reviewFilters.type = value as ReviewType;
		}
	}

	if (wallFilters.groups.source) {
		const { value, operator } = wallFilters.groups.source;
		if (operator === "in" && Array.isArray(value)) {
			reviewFilters.source = value.length === 1 ? (value[0] as ReviewSource) : (value as ReviewSource[]);
		} else if (operator === "eq" && typeof value === "string") {
			reviewFilters.source = value as ReviewSource;
		}
	}

	if (wallFilters.groups.content_type) {
		const { value, operator } = wallFilters.groups.content_type;
		if (operator === "in" && Array.isArray(value)) {
			reviewFilters.content_type = value.length === 1 ? (value[0] as ReviewContentType) : (value as ReviewContentType[]);
		} else if (operator === "eq" && typeof value === "string") {
			reviewFilters.content_type = value as ReviewContentType;
		}
	}

	if (wallFilters.groups.date) {
		const { value, operator } = wallFilters.groups.date;
		if (operator === "between" && Array.isArray(value)) {
			reviewFilters.date = `between:${value.join(",")}`;
		} else if (typeof value === "string") {
			reviewFilters.date = `${operator}:${value}`;
		}
	}

	if (wallFilters.groups.tags) {
		const { value } = wallFilters.groups.tags;
		if (Array.isArray(value)) {
			reviewFilters.tags = value;
		} else if (typeof value === "string") {
			// Handle legacy comma-separated format
			reviewFilters.tags = value.split(",").map((s) => s.trim());
		}
	}

	if (wallFilters.groups.customer_avatar_url) {
		const { value } = wallFilters.groups.customer_avatar_url;
		if (value === "with_avatar" || value === "without_avatar") {
			reviewFilters.customer_avatar_url = value;
		}
	}

	if (wallFilters.groups.customer_company_logo_url) {
		const { operator } = wallFilters.groups.customer_company_logo_url;
		if (operator === "not_null") {
			reviewFilters.customer_company_logo_url = "with_logo";
		} else if (operator === "is_null") {
			reviewFilters.customer_company_logo_url = "without_logo";
		}
	}

	return reviewFilters;
}

// Convert ReviewFilters to WallOfLoveFilters for saving
function convertReviewFiltersToWallFilters(reviewFilters: ReviewFilters): ReviewSelectionFilters | null {
	const groups: ReviewSelectionFilters["groups"] = {};

	if (reviewFilters.rating) {
		const ratingMap: Record<ReviewRatingFilter, number> = {
			"5_and_above": 5,
			"4_and_above": 4,
			"3_and_above": 3,
			"2_and_above": 2,
			"1_and_above": 1,
			with_rating: 1,
			without_rating: 0,
		};
		if (reviewFilters.rating in ratingMap && reviewFilters.rating !== "with_rating" && reviewFilters.rating !== "without_rating") {
			groups.rating = { operator: "gte", value: ratingMap[reviewFilters.rating] };
		}
	}

	if (reviewFilters.type) {
		groups.type = { operator: "eq", value: reviewFilters.type };
	}

	if (reviewFilters.source) {
		if (Array.isArray(reviewFilters.source)) {
			groups.source = { operator: "in", value: reviewFilters.source };
		} else {
			groups.source = { operator: "eq", value: reviewFilters.source };
		}
	}

	if (reviewFilters.content_type) {
		if (Array.isArray(reviewFilters.content_type)) {
			groups.content_type = { operator: "in", value: reviewFilters.content_type };
		} else {
			groups.content_type = { operator: "eq", value: reviewFilters.content_type };
		}
	}

	if (reviewFilters.date) {
		const [operator, ...rest] = reviewFilters.date.split(":");
		const value = rest.join(":");
		if (operator && value) {
			if (operator === "between") {
				// Store between values as an array
				groups.date = { operator, value: value.split(",") };
			} else {
				groups.date = { operator, value };
			}
		}
	}

	if (reviewFilters.tags && reviewFilters.tags.length > 0) {
		groups.tags = {
			operator: "in",
			value: reviewFilters.tags,
		};
	}

	if (reviewFilters.customer_avatar_url) {
		if (reviewFilters.customer_avatar_url === "with_avatar") {
			groups.customer_avatar_url = {
				operator: "not_null",
			};
		} else if (reviewFilters.customer_avatar_url === "without_avatar") {
			groups.customer_avatar_url = {
				operator: "is_null",
			};
		}
	}

	if (reviewFilters.customer_company_logo_url) {
		if (reviewFilters.customer_company_logo_url === "with_logo") {
			groups.customer_company_logo_url = {
				operator: "not_null",
			};
		} else if (reviewFilters.customer_company_logo_url === "without_logo") {
			groups.customer_company_logo_url = {
				operator: "is_null",
			};
		}
	}

	if (Object.keys(groups).length === 0) {
		return null;
	}

	return {
		logic: "and",
		groups,
	};
}

// Filter options
const SOURCE_OPTIONS: { value: ReviewSource; label: string }[] = [
	{ value: "form", label: "Form" },
	{ value: "manual", label: "Manual" },
	{ value: "import", label: "Import" },
	{ value: "google_reviews", label: "Google Reviews" },
	{ value: "twitter", label: "Twitter" },
	{ value: "linkedin", label: "LinkedIn" },
	{ value: "facebook", label: "Facebook" },
	{ value: "instagram", label: "Instagram" },
	{ value: "capterra", label: "Capterra" },
	{ value: "trustpilot", label: "Trustpilot" },
	{ value: "email", label: "Email" },
	{ value: "slack", label: "Slack" },
	{ value: "product-hunt", label: "Product Hunt" },
	{ value: "other", label: "Other" },
];

const TYPE_OPTIONS: { value: ReviewType | ""; label: string }[] = [
	{ value: "", label: "All" },
	{ value: "testimonial", label: "Testimonial" },
	{ value: "feedback", label: "Feedback" },
];

const CONTENT_TYPE_OPTIONS: { value: ReviewContentType; label: string }[] = [
	{ value: "text", label: "Text" },
	{ value: "video", label: "Video" },
	{ value: "audio", label: "Audio" },
];

const RATING_OPTIONS: { value: ReviewRatingFilter | ""; label: string }[] = [
	{ value: "", label: "All Ratings" },
	{ value: "5_and_above", label: "5 Stars Only" },
	{ value: "4_and_above", label: "4+ Stars" },
	{ value: "3_and_above", label: "3+ Stars" },
	{ value: "2_and_above", label: "2+ Stars" },
];

const PROFILE_OPTIONS: { value: ReviewAvatarFilter | ""; label: string }[] = [
	{ value: "", label: "All" },
	{ value: "with_avatar", label: "With Photo" },
	{ value: "without_avatar", label: "Without Photo" },
];

const COMPANY_OPTIONS: { value: ReviewCompanyLogoFilter | ""; label: string }[] = [
	{ value: "", label: "All" },
	{ value: "with_logo", label: "With Logo" },
	{ value: "without_logo", label: "Without Logo" },
];

const DATE_PRESET_OPTIONS = [
	{ value: "", label: "All Time" },
	{ value: "today", label: "Today" },
	{ value: "last_7_days", label: "Last 7 Days" },
	{ value: "last_30_days", label: "Last 30 Days" },
	{ value: "this_month", label: "This Month" },
	{ value: "this_year", label: "This Year" },
];

function getDateFilterValue(preset: string): string | undefined {
	const today = new Date();
	const formatDate = (date: Date) => date.toISOString().split("T")[0];

	switch (preset) {
		case "today":
			return `between:${formatDate(today)},${formatDate(today)}`;
		case "last_7_days": {
			const sevenDaysAgo = new Date(today);
			sevenDaysAgo.setDate(today.getDate() - 7);
			return `after:${formatDate(sevenDaysAgo)}`;
		}
		case "last_30_days": {
			const thirtyDaysAgo = new Date(today);
			thirtyDaysAgo.setDate(today.getDate() - 30);
			return `after:${formatDate(thirtyDaysAgo)}`;
		}
		case "this_month": {
			const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
			return `between:${formatDate(startOfMonth)},${formatDate(today)}`;
		}
		case "this_year": {
			const startOfYear = new Date(today.getFullYear(), 0, 1);
			return `between:${formatDate(startOfYear)},${formatDate(today)}`;
		}
		default:
			return undefined;
	}
}

function getPresetFromDateFilter(dateFilter?: string): string {
	if (!dateFilter) return "";
	for (const preset of DATE_PRESET_OPTIONS) {
		if (preset.value && getDateFilterValue(preset.value) === dateFilter) {
			return preset.value;
		}
	}
	return "";
}

// Section Icons
const SectionIcons = {
	type: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
			/>
		</svg>
	),
	format: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
			/>
		</svg>
	),
	source: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
			/>
		</svg>
	),
	rating: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
			/>
		</svg>
	),
	date: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
		</svg>
	),
	profile: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
		</svg>
	),
	company: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m1-4h1m1-4h1M21 21a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
		</svg>
	),
	tags: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
			/>
		</svg>
	),
};

// Accordion Section Component
function FilterSection({
	title,
	icon,
	isOpen,
	isActive,
	onToggle,
	children,
}: {
	title: string;
	icon: React.ReactNode;
	isOpen: boolean;
	isActive: boolean;
	onToggle: () => void;
	children: React.ReactNode;
}) {
	return (
		<div className="border-b border-gray-200 last:border-b-0">
			<button type="button" onClick={onToggle} className="flex items-center justify-between w-full px-0 py-3 text-left hover:opacity-80 transition-opacity">
				<div className="flex items-center gap-2.5">
					<span className="text-gray-400">{icon}</span>
					<span className="text-sm font-medium text-gray-900">{title}</span>
				</div>
				<div className="flex items-center gap-2">
					{isActive && <span className="w-2 h-2 rounded-full bg-gray-800" />}
					<svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
					</svg>
				</div>
			</button>
			{isOpen && <div className="pb-4">{children}</div>}
		</div>
	);
}

// Chip/Pill Option Component
function ChipOption({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selected ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
		>
			{children}
		</button>
	);
}

// Helper to check if value is selected in a multi-select filter
function isSelected<T extends string>(current: T | T[] | undefined, value: T): boolean {
	if (!current) return false;
	if (Array.isArray(current)) return current.includes(value);
	return current === value;
}

interface SelectReviewsModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	workspaceId: string;
	initialFilters: ReviewSelectionFilters | null;
	initialSort: ReviewSelectionSort | null;
	pinnedIds?: string[];
	tags: Tag[];
	onSave: (filters: ReviewSelectionFilters | null, sort: ReviewSelectionSort, totalMatchingCount: number) => void;
}

export default function SelectReviewsModal({ open, onOpenChange, workspaceId, initialFilters, initialSort, pinnedIds = [], tags, onSave }: SelectReviewsModalProps) {
	// Local state for the modal
	const [localFilters, setLocalFilters] = useState<ReviewFilters>(() => convertWallFiltersToReviewFilters(initialFilters));
	const [datePreset, setDatePreset] = useState(() => getPresetFromDateFilter(localFilters.date));
	const [openSections, setOpenSections] = useState<Set<string>>(new Set(["type"]));
	const [sortOption, setSortOption] = useState<SortOption>(() => convertReviewSortToSortOption(initialSort));
	const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
	const sortDropdownRef = useRef<HTMLDivElement>(null);

	// Reviews state
	const { indexReviewsForWallOfLove } = useReviews();
	const [allReviews, setAllReviews] = useState<Review[]>([]);
	const [isLoadingReviews, setIsLoadingReviews] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [totalReviewCount, setTotalReviewCount] = useState<number>(0);

	// Reset state when modal opens
	useEffect(() => {
		if (open) {
			setLocalFilters(convertWallFiltersToReviewFilters(initialFilters));
			setDatePreset(getPresetFromDateFilter(convertWallFiltersToReviewFilters(initialFilters).date));
			setSortOption(convertReviewSortToSortOption(initialSort));
		}
	}, [open, initialFilters, initialSort]);

	// Load reviews when modal opens or filters/sort change
	const loadReviews = useCallback(
		async (cursor?: string | null) => {
			if (!workspaceId) return;

			if (cursor) {
				setIsLoadingMore(true);
			} else {
				setIsLoadingReviews(true);
			}

			try {
				const reviewSort = convertSortOptionToReviewSort(sortOption);
				const response = await indexReviewsForWallOfLove(workspaceId, cursor, localFilters, reviewSort, pinnedIds);
				if (response) {
					if (cursor) {
						setAllReviews((prev) => [...prev, ...response.data]);
						setTotalReviewCount(response.meta.total);
					} else {
						setAllReviews(response.data);
					}
					setNextCursor(response.meta.next_cursor);
					setTotalReviewCount(response.meta.total);
				}
			} finally {
				setIsLoadingReviews(false);
				setIsLoadingMore(false);
			}
		},
		[workspaceId, indexReviewsForWallOfLove, localFilters, sortOption]
	);

	// Load reviews when modal opens or filters/sort change
	useEffect(() => {
		loadReviews();
	}, [loadReviews]);

	// Filter out pinned reviews from the list (sorting is handled by API)
	const availableReviews = useMemo(() => {
		if (!pinnedIds || pinnedIds.length === 0) return allReviews;
		return allReviews.filter((r) => !pinnedIds.includes(r.id));
	}, [allReviews, pinnedIds]);

	// Computed property for filtered review count
	const filteredReviewCount = useMemo(() => {
		return allReviews.length - availableReviews.length;
	}, [allReviews, availableReviews]);

	// Close dropdown when clicking outside
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
				setSortDropdownOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	// Filter handlers
	const toggleSection = (section: string) => {
		setOpenSections((prev) => {
			const next = new Set(prev);
			if (next.has(section)) {
				next.delete(section);
			} else {
				next.add(section);
			}
			return next;
		});
	};

	const handleFilterChange = <K extends keyof ReviewFilters>(key: K, value: ReviewFilters[K] | "") => {
		const newFilters = { ...localFilters };
		if (value === "" || value === undefined) {
			delete newFilters[key];
		} else {
			newFilters[key] = value;
		}
		setLocalFilters(newFilters);
	};

	const handleDatePresetChange = (preset: string) => {
		setDatePreset(preset);
		const dateValue = getDateFilterValue(preset);
		handleFilterChange("date", dateValue);
	};

	const handleTagToggle = (tagId: string) => {
		const currentTags = localFilters.tags || [];
		const newTags = currentTags.includes(tagId) ? currentTags.filter((id) => id !== tagId) : [...currentTags, tagId];
		handleFilterChange("tags", newTags.length > 0 ? newTags : undefined);
	};

	const handleTagsModeChange = (mode: ReviewTagsMode) => {
		handleFilterChange("tags_mode", mode);
	};

	const handleSourceToggle = (value: ReviewSource) => {
		const current = localFilters.source;
		const currentArray = Array.isArray(current) ? current : current ? [current] : [];
		const newArray = currentArray.includes(value) ? currentArray.filter((v) => v !== value) : [...currentArray, value];
		handleFilterChange("source", newArray.length > 0 ? (newArray.length === 1 ? newArray[0] : newArray) : undefined);
	};

	const handleContentTypeToggle = (value: ReviewContentType) => {
		const current = localFilters.content_type;
		const currentArray = Array.isArray(current) ? current : current ? [current] : [];
		const newArray = currentArray.includes(value) ? currentArray.filter((v) => v !== value) : [...currentArray, value];
		handleFilterChange("content_type", newArray.length > 0 ? (newArray.length === 1 ? newArray[0] : newArray) : undefined);
	};

	const handleProfileToggle = (value: ReviewAvatarFilter) => {
		handleFilterChange("customer_avatar_url", value);
	};

	const handleCompanyToggle = (value: ReviewCompanyLogoFilter) => {
		handleFilterChange("customer_company_logo_url", value);
	};

	// Save handler
	const handleSave = () => {
		const wallFilters = convertReviewFiltersToWallFilters(localFilters);
		const wallSort = convertSortOptionToWallSort(sortOption);

		onSave(wallFilters, wallSort, totalReviewCount - filteredReviewCount);
		onOpenChange(false);
	};

	// Load more handler
	const handleLoadMore = () => {
		if (nextCursor && !isLoadingMore) {
			loadReviews(nextCursor);
		}
	};

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
				<Dialog.Content
					className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-4xl h-[85vh] flex flex-col modal-content overflow-hidden"
				>
					{/* Header */}
					<div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
						<div>
							<Dialog.Title className="text-lg font-semibold text-gray-900">Review Filters and Sort</Dialog.Title>
							<Dialog.Description className="text-sm text-tertiary">Reviews will be displayed based on the selected filters and sort order.</Dialog.Description>
						</div>
						<Dialog.Close asChild>
							<button className="text-gray-400 hover:text-gray-600 transition-colors p-1" aria-label="Close">
								<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</Dialog.Close>
					</div>

					{/* Three-column body */}
					<div className="flex-1 flex overflow-hidden">
						{/* Left column: Filters */}
						<div className="w-72 border-r border-gray-200 p-6 overflow-y-auto shrink-0">
							<h3 className="font-semibold text-gray-900 mb-4">Filters</h3>

							{/* Type */}
							<FilterSection title="Type" icon={SectionIcons.type} isOpen={openSections.has("type")} isActive={!!localFilters.type} onToggle={() => toggleSection("type")}>
								<div className="flex flex-wrap gap-2">
									{TYPE_OPTIONS.map((option) => (
										<ChipOption
											key={option.value}
											selected={localFilters.type === option.value || (!localFilters.type && option.value === "")}
											onClick={() => handleFilterChange("type", option.value as ReviewType)}
										>
											{option.label}
										</ChipOption>
									))}
								</div>
							</FilterSection>

							{/* Format */}
							<FilterSection
								title="Format"
								icon={SectionIcons.format}
								isOpen={openSections.has("content_type")}
								isActive={Array.isArray(localFilters.content_type) ? localFilters.content_type.length > 0 : !!localFilters.content_type}
								onToggle={() => toggleSection("content_type")}
							>
								<div className="flex flex-wrap gap-2">
									<ChipOption
										selected={!localFilters.content_type || (Array.isArray(localFilters.content_type) && localFilters.content_type.length === 0)}
										onClick={() => handleFilterChange("content_type", undefined)}
									>
										All
									</ChipOption>
									{CONTENT_TYPE_OPTIONS.map((option) => (
										<ChipOption key={option.value} selected={isSelected(localFilters.content_type, option.value)} onClick={() => handleContentTypeToggle(option.value)}>
											{option.label}
										</ChipOption>
									))}
								</div>
							</FilterSection>

							{/* Source */}
							<FilterSection
								title="Source"
								icon={SectionIcons.source}
								isOpen={openSections.has("source")}
								isActive={Array.isArray(localFilters.source) ? localFilters.source.length > 0 : !!localFilters.source}
								onToggle={() => toggleSection("source")}
							>
								<div className="flex flex-wrap gap-2">
									<ChipOption
										selected={!localFilters.source || (Array.isArray(localFilters.source) && localFilters.source.length === 0)}
										onClick={() => handleFilterChange("source", undefined)}
									>
										All
									</ChipOption>
									{SOURCE_OPTIONS.map((option) => (
										<ChipOption key={option.value} selected={isSelected(localFilters.source, option.value)} onClick={() => handleSourceToggle(option.value)}>
											{option.label}
										</ChipOption>
									))}
								</div>
							</FilterSection>

							{/* Rating */}
							<FilterSection title="Rating" icon={SectionIcons.rating} isOpen={openSections.has("rating")} isActive={!!localFilters.rating} onToggle={() => toggleSection("rating")}>
								<div className="flex flex-wrap gap-2">
									{RATING_OPTIONS.map((option) => (
										<ChipOption
											key={option.value}
											selected={localFilters.rating === option.value || (!localFilters.rating && option.value === "")}
											onClick={() => handleFilterChange("rating", option.value as ReviewRatingFilter)}
										>
											{option.label}
										</ChipOption>
									))}
								</div>
							</FilterSection>

							{/* Date */}
							<FilterSection title="Date" icon={SectionIcons.date} isOpen={openSections.has("date")} isActive={!!localFilters.date} onToggle={() => toggleSection("date")}>
								<div className="flex flex-wrap gap-2">
									{DATE_PRESET_OPTIONS.map((option) => (
										<ChipOption key={option.value} selected={datePreset === option.value} onClick={() => handleDatePresetChange(option.value)}>
											{option.label}
										</ChipOption>
									))}
								</div>
							</FilterSection>

							{/* Profile */}
							<FilterSection title="Profile" icon={SectionIcons.profile} isOpen={openSections.has("profile")} isActive={!!localFilters.customer_avatar_url} onToggle={() => toggleSection("profile")}>
								<div className="flex flex-wrap gap-2">
									{PROFILE_OPTIONS.map((option) => (
										<ChipOption
											key={option.value}
											selected={localFilters.customer_avatar_url === option.value || (!localFilters.customer_avatar_url && option.value === "")}
											onClick={() => handleProfileToggle(option.value as ReviewAvatarFilter)}
										>
											{option.label}
										</ChipOption>
									))}
								</div>
							</FilterSection>

							{/* Company */}
							<FilterSection title="Company" icon={SectionIcons.company} isOpen={openSections.has("company")} isActive={!!localFilters.customer_company_logo_url} onToggle={() => toggleSection("company")}>
								<div className="flex flex-wrap gap-2">
									{COMPANY_OPTIONS.map((option) => (
										<ChipOption
											key={option.value}
											selected={localFilters.customer_company_logo_url === option.value || (!localFilters.customer_company_logo_url && option.value === "")}
											onClick={() => handleCompanyToggle(option.value as ReviewCompanyLogoFilter)}
										>
											{option.label}
										</ChipOption>
									))}
								</div>
							</FilterSection>

							{/* Tags */}
							{tags.length > 0 && (
								<FilterSection
									title="Tags"
									icon={SectionIcons.tags}
									isOpen={openSections.has("tags")}
									isActive={!!localFilters.tags && localFilters.tags.length > 0}
									onToggle={() => toggleSection("tags")}
								>
									<div className="space-y-3">
										<div className="flex flex-wrap gap-2">
											{tags.map((tag) => {
												const isTagSelected = localFilters.tags?.includes(tag.id) || false;
												const colorStyles = getTagColorStyles(tag.color);
												return (
													<button
														key={tag.id}
														type="button"
														onClick={() => handleTagToggle(tag.id)}
														className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-all ${colorStyles.bg} ${colorStyles.text} ${
															isTagSelected ? "ring-2 ring-offset-1 ring-gray-800" : "opacity-70 hover:opacity-100"
														}`}
													>
														{tag.label}
													</button>
												);
											})}
										</div>
										{localFilters.tags && localFilters.tags.length > 1 && (
											<div className="flex items-center gap-2 pt-2 border-t border-gray-200">
												<span className="text-xs text-tertiary">Match:</span>
												<button
													type="button"
													onClick={() => handleTagsModeChange("any")}
													className={`px-2 py-1 text-xs rounded ${
														localFilters.tags_mode !== "all" ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
													}`}
												>
													Any tag
												</button>
												<button
													type="button"
													onClick={() => handleTagsModeChange("all")}
													className={`px-2 py-1 text-xs rounded ${
														localFilters.tags_mode === "all" ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
													}`}
												>
													All tags
												</button>
											</div>
										)}
									</div>
								</FilterSection>
							)}
						</div>

						{/* Reviews column */}
						<div className="flex-1 flex flex-col overflow-hidden">
							<div className="py-3 px-6 border-b border-gray-200 shrink-0 flex items-center justify-between">
								<h3 className="font-semibold text-gray-900">Matching Reviews ({totalReviewCount})</h3>
								{/* Sort dropdown */}
								<div className="relative" ref={sortDropdownRef}>
									<button
										type="button"
										onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
										className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
									>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
										</svg>
										{SORT_OPTIONS.find((o) => o.value === sortOption)?.label}
										<svg className={`w-4 h-4 transition-transform ${sortDropdownOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
										</svg>
									</button>
									{sortDropdownOpen && (
										<div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
											{SORT_OPTIONS.map((option) => (
												<button
													key={option.value}
													type="button"
													onClick={() => {
														setSortOption(option.value);
														setSortDropdownOpen(false);
													}}
													className={`w-full text-left px-4 py-2 text-sm transition-colors ${
														sortOption === option.value ? "bg-gray-100 text-gray-900 font-medium" : "text-gray-600 hover:bg-gray-50"
													}`}
												>
													{option.label}
												</button>
											))}
										</div>
									)}
								</div>
							</div>

							<div className="flex-1 p-6 overflow-y-auto">
								{isLoadingReviews ? (
									<div className="space-y-2">
										{[1, 2, 3, 4].map((i) => (
											<div key={i} className="animate-pulse flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
												<div className="w-10 h-10 bg-gray-200 rounded-full" />
												<div className="flex-1 space-y-2">
													<div className="h-4 bg-gray-200 rounded w-1/3" />
													<div className="h-3 bg-gray-200 rounded w-2/3" />
												</div>
											</div>
										))}
									</div>
								) : availableReviews.length > 0 ? (
									<>
										<div className="space-y-2">
											{availableReviews.map((review) => (
												<AvailableReviewItem key={review.id} review={review} />
											))}
										</div>
										{nextCursor && (
											<div className="mt-4 text-center">
												<Button variant="soft" onClick={handleLoadMore} disabled={isLoadingMore}>
													{isLoadingMore ? "Loading..." : "Load More"}
												</Button>
											</div>
										)}
									</>
								) : (
									<div className="text-center py-12 text-tertiary">
										<svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
											/>
										</svg>
										<p className="text-sm font-medium">No reviews match your filters.</p>
										<p className="text-sm">Try adjusting your filter criteria.</p>
									</div>
								)}
							</div>
						</div>

					</div>

					{/* Footer */}
					<div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 shrink-0">
						<Button variant="soft" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button onClick={handleSave}>Done</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
